import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { isAuthenticatedViaCookie, logout } from '../../utils/utils';
import { logoNameMap } from '../../assets/SVGs/logos/Logo';

interface PaymentHeaderProps {
  title: string;
}

const PaymentHeader: React.FC<PaymentHeaderProps> = ({ title }) => {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const IconComponent = logoNameMap[theme.logo];
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleNavigation = (path: string) => {
    if (location.pathname !== path) {
      navigate(path);
    }
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false); // Close mobile menu after logout
  };

  const isCurrentPage = (path: string) => location.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm relative z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Title */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <IconComponent width={60} />
            </div>
            <h1 className="ml-2 sm:ml-4 text-lg sm:text-xl font-semibold text-gray-900 tracking-tight truncate">
              {title}
            </h1>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            <button
              onClick={() => handleNavigation('/')}
              className={`relative text-sm font-medium transition-all duration-200 ease-in-out ${
                isCurrentPage('/')
                  ? 'text-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:scale-105'
              }`}
            >
              Payments
              {isCurrentPage('/') && (
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-600 rounded-full"></div>
              )}
            </button>
            
            {isAuthenticatedViaCookie() && (
              <button
                onClick={() => handleNavigation('/payment-history')}
                className={`relative text-sm font-medium transition-all duration-200 ease-in-out ${
                  isCurrentPage('/payment-history')
                    ? 'text-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:scale-105'
                }`}
              >
                Payment History
                {isCurrentPage('/payment-history') && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-600 rounded-full"></div>
                )}
              </button>
            )}
            
            {isAuthenticatedViaCookie() && (
              <button
                onClick={() => handleNavigation('/subscriptions')}
                className={`relative text-sm font-medium transition-all duration-200 ease-in-out ${
                  isCurrentPage('/subscriptions')
                    ? 'text-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:scale-105'
                }`}
              >
                Subscriptions
                {isCurrentPage('/subscriptions') && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-600 rounded-full"></div>
                )}
              </button>
            )}
            
            <button
              onClick={() => handleNavigation('/donate')}
              className={`relative text-sm font-medium transition-all duration-200 ease-in-out ${
                isCurrentPage('/donate')
                  ? 'text-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:scale-105'
              }`}
            >
              Donate
              {isCurrentPage('/donate') && (
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-600 rounded-full"></div>
              )}
            </button>
            
            {isAuthenticatedViaCookie() && (
              <>
                <div className="h-4 w-px bg-gray-300"></div>
                <button
                  onClick={handleLogout}
                  className="text-sm font-medium text-gray-600 hover:text-red-600 transition-all duration-200 ease-in-out hover:scale-105"
                >
                  Logout
                </button>
              </>
            )}
            
            {!isAuthenticatedViaCookie() && (
              <button
                onClick={() => handleNavigation('/login')}
                className={`relative text-sm font-medium transition-all duration-200 ease-in-out ${
                  isCurrentPage('/login')
                    ? 'text-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:scale-105'
                }`}
              >
                Login
                {isCurrentPage('/login') && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-600 rounded-full"></div>
                )}
              </button>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-all duration-200"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {/* Hamburger icon */}
              <svg
                className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              {/* Close icon */}
              <svg
                className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div className={`md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200 shadow-lg">
          <button
            onClick={() => handleNavigation('/')}
            className={`w-full text-left block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
              isCurrentPage('/')
                ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Payments
          </button>
          
          {isAuthenticatedViaCookie() && (
            <button
              onClick={() => handleNavigation('/payment-history')}
              className={`w-full text-left block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                isCurrentPage('/payment-history')
                  ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Payment History
            </button>
          )}
          
          {isAuthenticatedViaCookie() && (
            <button
              onClick={() => handleNavigation('/subscriptions')}
              className={`w-full text-left block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                isCurrentPage('/subscriptions')
                  ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Subscriptions
            </button>
          )}
          
          <button
            onClick={() => handleNavigation('/donate')}
            className={`w-full text-left block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
              isCurrentPage('/donate')
                ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Donate
          </button>
          
          {isAuthenticatedViaCookie() && (
            <div className="border-t border-gray-200 pt-2 mt-2">
              <button
                onClick={handleLogout}
                className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
              >
                Logout
              </button>
            </div>
          )}
          
          {!isAuthenticatedViaCookie() && (
            <button
              onClick={() => handleNavigation('/login')}
              className={`w-full text-left block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                isCurrentPage('/login')
                  ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Login
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default PaymentHeader;